<template>
  <v-dialog
    v-model="localOpen"
    max-width="800px"
    :fullscreen="$vuetify.breakpoint.smAndDown"
    scrollable
    @input="handleDialogChange"
  >
    <v-card class="releases-modal-card">
      <div class="releases-header">
        <div class="releases-header-content">
          <div class="d-flex align-center mb-3">
            <div class="releases-icon-wrapper">
              <PhMegaphone
                :size="28"
                :weight="'fill'"
              />
            </div>
            <div class="ml-3">
              <h1 class="releases-title">{{ $t('comum.releases.title') }}</h1>
              <p class="releases-subtitle">{{ $t('comum.releases.subtitle') }}</p>
            </div>
          </div>
        </div>
      </div>
      <v-card-text class="releases-content pa-0">
        <div class="releases-intro">
          <div class="releases-intro-content">
            <div class="d-flex align-center justify-center mb-4">
              <img
                :src="logoProduto"
                alt="Tech Câmbio"
                class="releases-logo"
              />
            </div>
            <h2 class="releases-intro-title">{{ $t('comum.releases.introTitle') }}</h2>
            <p class="releases-intro-text">
              {{ $t('comum.releases.introText') }}
            </p>
          </div>
        </div>
        <div
          v-if="loading"
          class="d-flex justify-center py-8"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="48"
          />
        </div>

        <div
          v-else-if="error"
          class="text-center py-8"
        >
          <v-alert
            type="error"
            outlined
          >
            {{ error }}
          </v-alert>
        </div>

        <div
          v-else-if="hasNoReleases"
          class="text-center pa-8"
        >
          <v-alert
            type="info"
            outlined
          >
            {{ $t('comum.releases.noUpdates') }}
          </v-alert>
        </div>

        <div
          v-else
          class="releases-list"
        >
          <v-expansion-panels
            elevation="0"
            flat
            multiple
            class="releases-accordion"
          >
            <v-expansion-panel
              v-for="release in paginatedReleases"
              :key="release.id"
              class="release-panel"
            >
              <v-expansion-panel-header class="release-header">
                <div class="d-flex align-center justify-space-between w-100">
                  <div class="d-flex align-center flex-grow-1">
                    <div class="release-version-badge">
                      <PhMegaphone
                        :size="18"
                        class="mr-2"
                        :color="megaphoneIconColor"
                      />
                      <span class="release-version font-weight-bold">v.{{ release.release }}</span>
                    </div>
                    <v-chip
                      v-if="isRecentRelease(release.launchDate)"
                      small
                      label
                      color="success"
                      class="ml-3 release-new-badge"
                      elevation="0"
                    >
                      {{ $t('comum.releases.newVersion') }}
                    </v-chip>
                  </div>
                  <div class="release-date-wrapper">
                    <PhCalendarBlank
                      :size="16"
                      class="mr-1"
                    />
                    <span class="release-date">{{ formatReleaseDate(release.launchDate) }}</span>
                  </div>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="release-content">
                <div
                  class="release-description"
                  v-html="release.description"
                ></div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <div
            v-if="hasMultiplePages"
            class="releases-pagination"
          >
            <v-pagination
              v-model="currentPage"
              :length="totalPages"
              :total-visible="7"
              color="primary"
              elevation="0"
              @input="handlePageChange"
            />
          </div>
        </div>
      </v-card-text>
      <v-divider />
      <v-card-actions class="releases-actions">
        <v-spacer />
        <v-btn
          text
          class="btn-primary"
          @click="close"
        >
          {{ $t('comum.releases.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// Assets
import logoTechCambioLight from '@/assets/brand/techcambio-brand-light.svg';
import logoTechCambioDark from '@/assets/brand/techcambio-brand-dark.svg';

// Libs
import DOMPurify from 'dompurify';

// APIs
import releasesApi from '@/api/general/releases-api';

// Mixins
import mixinMessage from '@/mixin/mixin-message';

// Utils
import dateUtils from '@common/utils/date';

export default {
  name: 'ReleasesModal',
  mixins: [mixinMessage],
  props: {
    open: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      releases: [],
      loading: false,
      error: null,
      currentPage: 1,
      itemsPerPage: 5
    };
  },
  computed: {
    theme() {
      return this.$vuetify.theme.dark ? 'dark' : 'light';
    },

    /* Marca do PRODUTO, nao do tenant: e o Tech Cambio que entrega as versoes.
       Por isso o arquivo vem de `src/assets/brand` (empacotado, igual ao
       clara-logo.svg) e nao de `/brand`, que o CI substitui pelo logo do tenant
       antes do build — la o techcambio-brand-light.svg nem existe nos outros.
       O par claro/escuro continua porque o letreiro e preto no light. */
    logoProduto() {
      return this.theme === 'dark' ? logoTechCambioDark : logoTechCambioLight;
    },

    localOpen: {
      get() {
        return this.open;
      },
      set(value) {
        this.$emit('update:open', value);
      }
    },

    paginatedReleases() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;

      return this.releases.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.releases.length / this.itemsPerPage);
    },

    hasNoReleases() {
      return this.releases.length === 0;
    },

    hasMultiplePages() {
      return this.totalPages > 1;
    },

    megaphoneIconColor() {
      return this.theme === 'dark' ? 'white' : '#1e22aa';
    }
  },
  watch: {
    open(newValue) {
      if (!newValue) return;

      this.currentPage = 1;
      this.loadReleases();
    }
  },
  methods: {
    handleDialogChange(value) {
      this.$emit('update:open', value);
    },
    close() {
      this.$emit('update:open', false);
    },
    async loadReleases() {
      this.loading = true;
      this.error = null;

      try {
        const response = await releasesApi.getReleases();

        this.releases = response.data?.map(r => ({ ...r, description: DOMPurify.sanitize(r.description) })) || [];
      } catch {
        this.error = this.$t('comum.releases.loadError');
      } finally {
        this.loading = false;
      }
    },
    formatReleaseDate(dateString) {
      if (!dateString) return '';

      return dateUtils.maskDateIso(dateString);
    },
    isRecentRelease(launchDate) {
      if (!launchDate) return false;

      const releaseDate = new Date(launchDate);
      const now = new Date();
      const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);

      return releaseDate >= twoWeeksAgo;
    },
    handlePageChange(page) {
      this.currentPage = page;

      const releasesList = document.querySelector('.releases-list');

      if (releasesList) releasesList.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }
};
</script>

<style lang="scss" scoped>
/* IDENTIDADE FIXA DO PRODUTO
   Este modal fala do Tech Cambio — quem entrega as versoes e o produto, nao o
   tenant que o revende. Entao a marca aqui NAO pode sair de `--tc-action`, que
   e derivado do `primary` do tenant e varia muito: #1E22AA no techcambio, mas
   #000000 no symcapital, #0A0A0A no dolspay, #0F3752 no bdayan. O gradiente do
   cabecalho virava preto-para-azul nesses casos, e no tema escuro chegava a
   dourado (#E3C390) e laranja (#EE5F3F).

   Os valores abaixo sao o royal do Tech Cambio nas duas variantes de tema. Nao
   troque por token: aqui e literal de proposito.

   As SUPERFICIES (fundo, texto, linha) continuam vindo do tema — sao neutras e
   praticamente iguais entre os tenants (#ECEFF1/#FFFFFF no claro), e fixa-las
   quebraria o modo escuro. O que se fixa e a MARCA, nao a superficie. */
.releases-modal-card {
  overflow: hidden;
  --rel-marca: #1e22aa;
  --rel-marca-fundo: #0e1280;
}

.releases-modal-card.theme--dark {
  --rel-marca: #7a80f0;
  --rel-marca-fundo: #171a6b;
}

.releases-header {
  background: linear-gradient(135deg, var(--rel-marca) 0%, var(--rel-marca-fundo) 100%);
  color: white;
  padding: 32px 24px;
}

.releases-header-content {
  max-width: 100%;
}

.releases-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: var(--tc-radius-md);
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.releases-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  margin: 0;
  line-height: 1.2;
}

.releases-subtitle {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 4px 0 0 0;
  font-weight: 400;
}

.releases-content {
  background-color: var(--v-backgroundSecondary-base);
}

.releases-intro {
  background: linear-gradient(to bottom, var(--v-backgroundPrimary-lighten1) 0%, var(--v-backgroundSecondary-base) 100%);
  padding: 32px 24px;
  text-align: center;
}

.releases-intro-content {
  max-width: 600px;
  margin: 0 auto;
}

.releases-logo {
  height: 50px;
  width: auto;
}

.releases-intro-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--rel-marca);
  margin: 16px 0 12px 0;
}
.theme--dark .releases-intro-title{
  color: white;
}

.releases-intro-text {
  font-size: 1rem;
  line-height: 1.6;
  margin: 0;
}

.releases-list {
  padding: 24px;
}

/* Nenhuma regra de painel aqui, de proposito. A secao 13 do design system ja
   monta o acordeao como PILHA ENCOSTADA — borda `--tc-line`, fundo `--tc-surface`,
   painel sem margem, topo sem borda a partir do segundo e o raio aplicado so nas
   pontas do grupo. E o mesmo acordeao que a pagina /ui mostra.

   O que existia aqui competia com isso e so piorava: `margin-bottom: 12px` abria
   os vaos entre as versoes, e a borda/fundo em `--v-backgroundPrimary-base` (a cor
   do proprio fundo) apagava a linha que separa uma versao da outra. O
   `border-radius` nem chegava a valer — o do DS vem com `!important`. */

.release-header {
  padding: 16px 20px;
}

/* Sem `padding` nem `border-radius`: este bloco nao tem fundo, entao os 6px
   verticais eram espaco morto encaixado DENTRO dos 12px do cabecalho — 12px a
   mais por item, que e a folga que sobrava abaixo do numero da versao. Sem eles
   o item volta aos 44px de altura minima do DS e a pilha fica no mesmo ritmo do
   resto do sistema. */
.release-version-badge {
  display: flex;
  align-items: center;
}

.release-version {
  font-size: 1.1rem;
  color: var(--rel-marca);
}
.theme--dark .release-version {
  color: white;
}
.release-new-badge {
  font-weight: 600;
}

.release-date-wrapper {
  display: flex;
  align-items: center;
  color: var(--v-text-secondary);
  font-size: 0.875rem;
}

.release-date {
  font-size: 0.875rem;
  color: var(--v-text-secondary);
}

/* O conteudo do painel sao TRES caixas encaixadas: `.v-expansion-panel-content`
   (esta), o `__wrap` que o Vuetify monta dentro dela, e a `.release-description`.
   Cada uma trazia o proprio padding inferior — 20 + 16 + 16 — e ainda vinham a
   margem do `<ul>` (12) e a do ultimo `<li>` (8) do HTML das notas: 72px de vazio
   abaixo da ultima linha de cada versao. O respiro e declarado aqui e so aqui;
   as caixas de dentro ficam zeradas. */
.release-content {
  padding: 0 20px 20px 20px;
}

.release-content ::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}

.release-description {
  /* So em cima: separa do cabecalho. Embaixo quem cuida e o `.release-content`. */
  padding-top: 16px;
  line-height: 1.7;
}

/* O HTML das notas vem do backend e termina quase sempre num `<ul>` ou `<p>`,
   cuja margem inferior cairia dentro do padding do painel e o dobraria. */
.release-description ::v-deep > *:last-child {
  margin-bottom: 0;
}

.release-description ::v-deep li:last-child {
  margin-bottom: 0;
}

.releases-actions {
  padding: 16px 24px;
  background-color: var(--v-backgroundSecondary-base);
}

.releases-pagination {
  display: flex;
  justify-content: center;
  padding: 24px 0 8px 0;
  margin-top: 16px;
  border-top: 1px solid var(--v-backgroundPrimary-base);
}

.release-description ::v-deep h1,
.release-description ::v-deep h2,
.release-description ::v-deep h3 {
  margin-top: 16px;
  margin-bottom: 8px;
}

.release-description ::v-deep h1 {
  font-size: 1.5rem;
}

.release-description ::v-deep h2 {
  font-size: 1.3rem;
}

.release-description ::v-deep h3 {
  font-size: 1.1rem;
}

.release-description ::v-deep p {
  margin-bottom: 12px;
}

.release-description ::v-deep ul,
.release-description ::v-deep ol {
  margin-left: 24px;
  margin-bottom: 12px;
}

.release-description ::v-deep li {
  margin-bottom: 8px;
}

.release-description ::v-deep strong {
  font-weight: 600;
}

.release-description ::v-deep em {
  font-style: italic;
}

::v-deep .v-pagination__item {
  border: 1px solid var(--v-backgroundPrimary-base);
  box-shadow: none ;
}

::v-deep .v-pagination__navigation {
  box-shadow: none ;
}
</style>
